import { WritableSignal } from '@angular/core';
import { SavedViewSerialized } from './types';
import { MetricsGridConfig } from '@/app/core/grid/MetricsGridConfig';
import { GridSortingService } from '@/app/core/services/grid-sorting.service';
import { IGridColumnGroup } from '@/app/core';

/**
 * Configuration required for grid serialization
 */
export interface GridSerializerContext {
  gridName: string;
  gridConfig: WritableSignal<MetricsGridConfig> | (() => MetricsGridConfig);
  gridSortingService: GridSortingService;
  componentName?: string; // Optional: for better console logging
}

const DEBUG = false;

/**
 * Factory function that creates a getSerializer method for grid components.
 * This abstracts the common serialization logic used across multiple grid components.
 *
 * @param context - The context object containing grid configuration and services
 * @returns A function that returns the serializer function
 *
 * @example
 * ```typescript
 * class MyGridComponent {
 *   private getSerializer = createGridSerializer({
 *     gridName: this.gridName,
 *     gridConfig: this.gridConfig,
 *     gridSortingService: this.gridSortingService,
 *     componentName: 'MyGridComponent'
 *   });
 * }
 * ```
 */
export function createGridSerializer(
  context: GridSerializerContext,
  ignoreLocked: boolean = false
): () => () => SavedViewSerialized {
  return (): (() => SavedViewSerialized) => {
    return (): SavedViewSerialized => {
      // Handle both signals and plain objects
      const config = context.gridConfig();

      const result: Partial<SavedViewSerialized> = {
        grid_id: context.gridName,
        columns: [],
      };

      const titleCounts = new Map<string, number>();

      // First, count occurrences of each columnSelectorTitle
      config?.groups.forEach((group) => {
        const title = group.columnSelectorTitle || '';
        if (title) {
          titleCounts.set(title, (titleCounts.get(title) || 0) + 1);
        }
      });

      const shouldSave = (item: IGridColumnGroup): boolean => {
        if (ignoreLocked) {
          return item.visible;
        } else {
          return item.visible || item.locked;
        }
      };

      config?.groups.forEach((group, index) => {
        // some grids have locked groups that need to be included in the serialization even if they are not marked visible
        if (shouldSave(group)) {
          const title =
            group.id || group.columnSelectorTitle || `__group_${index}__`;
          let serializedTitle = title;

          if (titleCounts.get(title) > 1) {
            // const currentIndex = (titleIndices.get(title) || 0) + 1;
            serializedTitle = `${title}___${index}`;
            // titleIndices.set(title, currentIndex);
          }
          result.columns.push(serializedTitle);
        }
      });

      // Get the current sorting state from the service to ensure we have the latest values
      const currentSortField =
        context.gridSortingService.getSortFieldOrDefault();
      const currentSortOrder =
        context.gridSortingService.getSortDirectionOrDefault();

      result.sort = [
        {
          key: currentSortField,
          direction: currentSortOrder === 1 ? 'asc' : 'desc',
          priority: 1,
        },
      ];

      if (DEBUG) {
        console.log(
          'saved-views-serializer.ts getSerializer result for grid',
          context.componentName || context.gridName,
          ':\n',
          config,
          result
        );
      }

      return result as SavedViewSerialized;
    };
  };
}
